import React, { useState, useEffect } from "react";
import { getGroupColor } from "../../../theme/groupingColors";
import "./clashing.scss";
import {
  Button,
  FormControl,
  MenuItem,
  Select,
} from "@mui/material";

function ClashToSolve(props) {
  const { 
    groupId, 
    groups, 
    currentValue, 
    incomingValue, 
    solve, 
    usedNewGroups = [], 
    recommendedGroup 
  } = props;

  const [freeGroups, setFreeGroups] = useState([]);
  const [chosenNewGroup, setChosenNewGroup] = useState(null);

  // Update free groups when groups or usedNewGroups change
  useEffect(() => {
    const availableGroups = [];

    for (let i = 0; i < groups.length; i++) {
      const group = groups[i];
      if (group.value === null && !usedNewGroups.includes(group.groupNumber)) {
        availableGroups.push(group.groupNumber);
      }
    }

    setFreeGroups(availableGroups);
  }, [groups, usedNewGroups]);

  // Update chosen group when recommendedGroup changes or when component first loads
  useEffect(() => {
    if (recommendedGroup !== null && freeGroups.includes(recommendedGroup)) {
      setChosenNewGroup(recommendedGroup);
    } else if (freeGroups.length > 0 && (chosenNewGroup === null || !freeGroups.includes(chosenNewGroup))) {
      // Sort to get the lowest available group number
      const sortedGroups = [...freeGroups].sort((a, b) => a - b);
      setChosenNewGroup(sortedGroups[0]);
    }
  }, [recommendedGroup, freeGroups, chosenNewGroup]);

  const handleKeepCurrent = () => {
    solve(groupId, chosenNewGroup, "CURRENT_STAYS")
  };
  
  const handleKeepIncoming = () => {
    solve(groupId, chosenNewGroup, "NEW_STAYS")
  };
  
  const handleNewGroupSolution = () => {
    solve(groupId, chosenNewGroup, "NEW_GROUP")
  }

  const handleGroupChange = (event) => {
    setChosenNewGroup(event.target.value);
  };

  return (
    <div className="clashToSolve">
      <div className="groupNumberSection">
        <div className="text">Group in conflict: </div>
        <div
          className="groupNumber"
          style={{ backgroundColor: getGroupColor(groupId) }}
        >
          {groupId}
        </div>
      </div>
      <div className="properties-table-container">
        <table className="properties-table">
          <thead>
            <tr>
              <th>Property</th>
              <th>Current</th>
              <th>Incoming</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td>Value:</td>
              <td>{currentValue}</td>
              <td>{incomingValue}</td>
            </tr>
            <tr>
              <td>Fixed:</td>
              <td>{props.currentFixed !== null ? String(props.currentFixed) : "✖"}</td>
              <td>{props.incomingFixed !== undefined ? String(props.incomingFixed) : "✖"}</td>
            </tr>
            <tr>
              <td>Hard Max:</td>
              <td>{props.currentHardMax || "✖"}</td>
              <td>{props.incomingHardMax || "✖"}</td>
            </tr>
            <tr>
              <td>Hard Min:</td>
              <td>{props.currentHardMin || "✖"}</td>
              <td>{props.incomingHardMin || "✖"}</td>
            </tr>
          </tbody>
        </table>
      </div>
      <div className="solutionArea">
        <Button
          variant="contained"
          size="small"
          sx={{ m: 1 }}
          onClick={() => handleKeepCurrent()}
          className="solutionButton"
        >
          Keep Current
        </Button>
        <Button
          variant="contained"
          size="small"
          sx={{ m: 1 }}
          onClick={() => handleKeepIncoming()}
          className="solutionButton"
        >
          Keep Incoming
        </Button>
      </div>
      {freeGroups.length > 0 && chosenNewGroup !== null ? (
        <div className="newGroupChoice">
          <div className="newGroupTitle">
            Or assign a new group number to the incoming group:
          </div>
          <div className="choosingGroup">
            <div className="selectArea">
              <FormControl variant="standard" className="formControl">
                <Select
                  MenuProps={{
                    container: () => document.getElementById("clashModal"),
                    PaperProps: {
                      style: { maxHeight: 400, width: 550 }
                    },
                    MenuListProps: { 
                      style: { 
                        display: 'grid',
                        gridTemplateColumns: 'repeat(10, 1fr)',
                        gap: '5px',
                        padding: '10px'
                      } 
                    }
                  }}
                  labelId="group-select-label"
                  value={chosenNewGroup}
                  onChange={handleGroupChange}
                  style={{ 
                    backgroundColor: getGroupColor(chosenNewGroup),
                    borderRadius: '4px',
                    height: '36px',
                    minWidth: '80px'
                  }}
                  sx={{
                    '.MuiSelect-select': {
                      color: '#000',
                      textAlign: 'center',
                      paddingRight: '24px', // Compensate for the dropdown icon space
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center'
                    }
                  }}
                  className="select"
                >
                  {Array.from({ length: 100 }, (_, i) => {
                    const groupNumber = i + 1;
                    const isAvailable = freeGroups.includes(groupNumber);
                    
                    return isAvailable ? (
                      <MenuItem
                        key={groupNumber}
                        value={groupNumber}
                        className="menuItem"
                        style={{ 
                          backgroundColor: getGroupColor(groupNumber),
                          fontSize: '12px',
                          padding: '2px 0',
                          minHeight: '30px',
                          display: 'flex',
                          justifyContent: 'center',
                          alignItems: 'center',
                          borderRadius: '2px'
                        }}
                      >
                        {groupNumber}
                      </MenuItem>
                    ) : (
                      <MenuItem
                        key={groupNumber}
                        disabled
                        style={{ 
                          backgroundColor: 'transparent',
                          visibility: 'hidden',
                          minHeight: '30px',
                        }}
                      />
                    );
                  })}
                </Select>
              </FormControl>
            </div>
            <div className="buttonContainer">
              <Button
                variant="contained"
                size="small"
                sx={{ 
                  height: '36px',
                  minWidth: '120px'
                }}
                onClick={() => handleNewGroupSolution()}
                className="solutionButton"
              >
                New Group
              </Button>
            </div>
          </div>
        </div>
      ) : (
        <div className="no-groups-message">
          No free groups are available
        </div>
      )}
    </div>
  );
}

export default ClashToSolve;
